/**
 * @fileoverview Module de gestion du scanner de code-barres (ISBN) et de la recherche externe.
 * Utilise la bibliothèque externe Html5Qrcode pour la lecture via la caméra,
 * et l'API publique OpenLibrary pour la récupération des métadonnées de la bande dessinée.
 */

import { fetchImageAsBase64 } from './image.js';
import { showLoader, updateLoaderStatus, hideLoader, showNotification } from './ui.js';
import { fetchBookData } from './api-books.js';
import { findComicByIsbn } from './db.js';

/**
 * Fonction de rappel (callback) exécutée lorsqu'un livre est trouvé avec succès.
 * @callback OnBookFoundCallback
 * @param {Object} bookData - Les métadonnées de la bande dessinée récupérées.
 * @param {string} bookData.isbn - Le numéro ISBN scanné ou saisi.
 * @param {string} bookData.title - Le titre principal de la BD.
 * @param {string} bookData.authors - Le(s) auteur(s), séparés par des virgules.
 * @param {string} bookData.publisher - La maison d'édition.
 * @param {string} bookData.series - La série (généralement mappée depuis le sous-titre du livre).
 * @param {string|null} bookData.coverBase64 - L'image de couverture encodée en Base64 WebP, ou null si indisponible.
 */

/**
 * Charge html5-qrcode.min.js de façon différée (première utilisation seulement).
 * Les appels suivants sont instantanés — le script est déjà présent dans le DOM.
 * Affiche le loader pendant le téléchargement (~100 Ko).
 * @private
 * @returns {Promise<void>}
 */
async function _loadLibrary() {
    if (window.Html5Qrcode) return;

    showLoader('Chargement du scanner…');
    return new Promise((resolve, reject) => {
        const script = document.createElement('script');
        script.src = 'js/html5-qrcode.min.js';
        script.onload  = () => { hideLoader(); resolve(); };
        script.onerror = () => { hideLoader(); reject(new Error('Échec du chargement de html5-qrcode')); };
        document.head.appendChild(script);
    });
}

/**
 * Expose le chargement de la librairie pour les modules qui en ont besoin sans passer par initScanner.
 * @returns {Promise<void>}
 */
export function loadScannerLibrary() {
    return _loadLibrary();
}

/**
 * Branche un <input type="file"> pour décoder un code-barres depuis une image et lancer
 * le pipeline complet (fetch métadonnées + couverture → onBookFound).
 * Réutilise le même lazy loader et le même flow que le scan caméra.
 *
 * @param {string} inputId - ID de l'<input type="file" accept="image/*">.
 * @param {OnBookFoundCallback} onBookFound - Même signature que le callback d'initScanner.
 */
export function bindImageScan(inputId, onBookFound) {
    const input = document.getElementById(inputId);
    if (!input) return;

    input.addEventListener('change', async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;

        try {
            await _loadLibrary();
        } catch (err) {
            console.error('Impossible de charger le scanner :', err);
            showNotification('Impossible de charger le scanner.', 'error');
            input.value = '';
            return;
        }

        showLoader('Analyse de l\'image…');
        try {
            const scanner = new Html5Qrcode('camera-reader');
            const isbn = await scanner.scanFile(file, /* showImage= */ false);
            // Délègue au même pipeline que le scan caméra (vérif collection + fetch + couverture)
            await _fetchAndNotify(isbn, onBookFound);
        } catch {
            hideLoader();
            showNotification('Aucun code-barres détecté dans cette image.', 'error');
        } finally {
            input.value = '';
        }
    });
}

/**
 * Initialise l'interface matérielle du scanner de code-barres et le champ de recherche manuelle.
 * Doit être appelée une seule fois lors du démarrage de l'application, une fois le DOM chargé.
 * * @param {OnBookFoundCallback} onBookFound - Le callback appelé avec les données du livre formatées.
 */
export function initScanner(onBookFound) {
    const btnStartScan = document.getElementById('btn-start-scan');
    const inputIsbn = document.getElementById('input-isbn-search');
    const btnSearchIsbn = document.getElementById('btn-search-isbn');

    let html5QrCode = null;

    // Écouteur pour le bouton d'activation/désactivation de la caméra
    btnStartScan.addEventListener('click', async () => {
        // Si le scanner tourne déjà, on l'arrête
        if (html5QrCode?.isScanning) {
            _stopScanner(html5QrCode, btnStartScan);
            return;
        }

        // Chargement différé de la lib au premier clic (aucun effet les fois suivantes)
        try {
            await _loadLibrary();
        } catch (err) {
            console.error('Impossible de charger le scanner :', err);
            showNotification('Impossible de charger le scanner.', 'error');
            return;
        }

        // Démarrage du scanner une fois la lib disponible
        html5QrCode = _startScanner(btnStartScan, async (isbn) => {
            _stopScanner(html5QrCode, btnStartScan); // On coupe la caméra dès qu'un code est lu
            await _fetchAndNotify(isbn, onBookFound); // On lance la recherche
        });
    });

    // Écouteur pour le bouton de recherche ISBN manuelle (si la caméra échoue)
    btnSearchIsbn.addEventListener('click', async () => {
        const isbn = inputIsbn.value.trim();

        if (!isbn) {
            alert("Veuillez entrer un numéro ISBN.");
            return;
        }

        await _fetchAndNotify(isbn, onBookFound);
        inputIsbn.value = ''; // Réinitialisation du champ après la recherche
    });
}

// ==========================================
// --- FONCTIONS PRIVÉES (Helpers) ---
// ==========================================

/**
 * Démarre le flux vidéo de la caméra arrière et commence la détection de code-barres.
 * * @private
 * @param {HTMLElement} btnStartScan - L'élément bouton pour mettre à jour son libellé.
 * @param {Function} onSuccess - Fonction déclenchée lorsqu'un code-barres est décodé avec succès.
 * @returns {Html5Qrcode} L'instance active du scanner pour un contrôle ultérieur.
 */
function _startScanner(btnStartScan, onSuccess) {
    // Nécessite l'inclusion préalable de la librairie Html5Qrcode dans l'environnement
    const scanner = new Html5Qrcode("camera-reader");

    // Configuration : 10 images/seconde, zone de scan rectangulaire typique d'un code-barres
    const config = { fps: 10, qrbox: { width: 250, height: 100 } };

    scanner.start(
        { facingMode: "environment" }, // Privilégie la caméra arrière sur les mobiles
        config,
        onSuccess,
        () => { } // Callback d'échec ignoré volontairement (se déclenche à chaque frame sans code-barres)
    ).then(() => {
        btnStartScan.textContent = "🛑 Arrêter le scanner";
    }).catch((err) => {
        console.error("Erreur de démarrage de la caméra :", err);
        alert("Impossible d'accéder à la caméra.");
    });

    return scanner;
}

/**
 * Arrête le flux vidéo, libère la caméra et réinitialise l'interface du scanner.
 * * @private
 * @param {Html5Qrcode} scanner - L'instance active du scanner à stopper.
 * @param {HTMLElement} btnStartScan - L'élément bouton pour restaurer son libellé initial.
 */
function _stopScanner(scanner, btnStartScan) {
    if (!scanner) return;

    scanner.stop().then(() => {
        btnStartScan.textContent = "📷 Scanner un code-barres (ISBN)";
        scanner.clear(); // Nettoie le canvas vidéo du DOM
    }).catch((err) => {
        console.error("Erreur d'arrêt du scanner :", err);
    });
}

/**
 * Interroge des API publiques (Google Books, OpenLibrary et BNF) à partir d'un ISBN et appelle la
 * méthode dédiée pour traiter le JSON retourné
 * @param {string} isbn - Le numéro de code-barres à rechercher.
 * @returns {Promise<void>}
 */
// async function fetchBookData(isbn) {
//     return await _tryOpenLibrary(isbn)
//         ?? await _tryGoogleBooks(isbn)
//         ?? await _tryBnF(isbn)
//         ?? null;
// }

/**
 * Interroge l'API publique Google Books à partir d'un ISBN, traite le JSON retourné,
 * récupère l'URL de l'image de couverture
 * * @private
 * @param {string} isbn - Le numéro de code-barres à rechercher.
 * @returns {Promise<void>}
 */
// async function _tryGoogleBooks(isbn) {
//     updateLoaderStatus('Interrogation de Google Books…');
//     try {
//         const response = await fetch(`https://www.googleapis.com/books/v1/volumes?q=isbn:${isbn}&maxResults=1`);
//         const data = await response.json();
//         const book = data.items?.[0]?.volumeInfo;

//         if (!book) {
//             console.log(`BD ISBN ${isbn} non trouvée sur Google Books`)
//             return null
//         };

//         let title = book.title;
//         let series = book.subtitle ?? '';

//         if (book.title && book.subtitle) {
//             series = book.title;
//             title = book.subtitle;
//         }

//         return {
//             isbn: isbn,
//             title: title,
//             authors: book.authors?.join(', ') ?? '',
//             publisher: book.publisher ?? '',
//             series: series,
//             coverUrl: book.imageLinks?.thumbnail?.replace('http:', 'https:') ?? null,
//         };
//     } catch (error) {
//         console.error("Erreur réseau lors de l'appel à Google Books :", error);
//         return null
//     }
// }

/**
 * Interroge l'API publique Open Library à partir d'un ISBN, traite le JSON retourné,
 * récupère l'URL de l'image de couverture.
 * * @private
 * @param {string} isbn - Le numéro de code-barres à rechercher.
 * @returns {Promise<void>}
 */
// async function _tryOpenLibrary(isbn) {
//     updateLoaderStatus('Interrogation de Open Library…');
//     try {
//         const response = await fetch(`https://openlibrary.org/api/books?bibkeys=ISBN:${isbn}&jscmd=data&format=json`);
//         const data = await response.json();
//         const bookKey = `ISBN:${isbn}`;

//         // Si l'ISBN n'existe pas dans leur base de données
//         if (!data[bookKey]) {
//             console.log(`BD ISBN ${isbn} non trouvée sur Open Library`)
//             return null
//         }

//         const book = data[bookKey];

//         let title = book.title;
//         let serie = book.subtitle ?? '';
//         let volume = "";

//         if (book.subtitle && book.title) {
//             let dispatch = _extraireSerieAndVolumeFromOpenLibrary(book.title);

//             if (dispatch.serie && dispatch.volume) {
//                 title = book.subtitle;
//                 serie = dispatch.serie;
//                 volume = dispatch.volume;
//             } else {
//                 let dispatch = _extraireSerieAndVolumeFromOpenLibrary(book.subtitle);

//                 if (dispatch.serie && dispatch.volume) {
//                     title = book.title;
//                     serie = dispatch.serie;
//                     volume = dispatch.volume;
//                 }
//             }
//         }

//         // Mappage (parsing) des données brutes de l'API vers notre modèle de données local
//         return {
//             isbn: isbn,
//             title: title,
//             authors: book.authors?.map(a => a.name).join(', ') ?? '',
//             publisher: book.publishers?.map(p => p.name).join(', ') ?? '',
//             series: serie,
//             volume: volume,
//             coverUrl: book.cover?.large?.replace('http:', 'https:') ?? null,
//         };
//     } catch (error) {
//         console.error("Erreur réseau lors de l'appel à Open Library :", error);
//         return null
//     }
// }

/**
 * @todo
 * Interroge l'API publique de la BNF à partir d'un ISBN, traite le XML retourné,
 * récupère l'URL de l'image de couverture.
 * * @private
 * @param {string} isbn - Le numéro de code-barres à rechercher.
 * @returns {Promise<void>}
 */
// async function _tryBnF(isbn) {
//     updateLoaderStatus('Interrogation du catalogue BNF…');
//     // https://api.bnf.fr/api-sru-catalogue-general
//     return null
// }



/**
 * Interroge l'API publique OpenLibrary à partir d'un ISBN, traite le JSON retourné,
 * tente de télécharger l'image de couverture, puis transmet l'objet finalisé au callback.
 * * @private
 * @param {string} isbn - Le numéro de code-barres à rechercher.
 * @param {OnBookFoundCallback} onBookFound - La fonction vers laquelle envoyer les données de la BD.
 * @returns {Promise<void>}
 */
async function _fetchAndNotify(isbn, onBookFound) {
    const existing = await findComicByIsbn(isbn);

    if (existing) {
        const title = existing.series
            ? `${existing.series}${existing.volume ? ` - Tome ${existing.volume}` : ''}`
            : existing.title;

        showNotification(
            `Cette BD est déjà dans ta collection : "${title}"`,
            'warning'
        );
        return; // On arrête ici — pas d'appel API, pas de remplissage du formulaire
    }

    showLoader('Initialisation de la recherche…');

    let result;

    try {
        result = await fetchBookData(isbn);
    } finally {
        hideLoader();
    }

    console.log(`🔎 Recherche des informations de la BD ISBN ${isbn}.`)

    if (result) {
        if (result.coverUrl) {
            showLoader('Récupération de l\'image…');
            result.coverBase64 = await fetchImageAsBase64(result.coverUrl);
            hideLoader();
        }

        if (result.coverBase64) {
            showNotification('Informations et couverture récupérées !', 'success');
        } else {
            showNotification('Informations récupérées, mais sans couverture.', 'warning');
        }
    } else {
        result = { isbn: isbn }
        showNotification(`Aucune information trouvée pour l'ISBN ${isbn}.`, 'error');
    }

    // Renvoi de l'objet propre vers le module appelant (app.js / ui.js)
    onBookFound(result);
}



